import { HOLAT_RANGLARI } from "@/shared/charts/palette";

/**
 * ⚠️ **Tip ATAYLAB nusxalangan.** `modules/employees` `modules/attendance`
 * ning ichki tipini import qilsa, ikki modul o'zaro bog'lanib qolardi va
 * davomat modulidagi har o'zgarish profil sahifasini ham sindirardi
 * (`modules/dashboard/types` da xuddi shu naqsh). Bu yerda KERAKLI
 * to'plamning O'ZI e'lon qilinadi — javobning qolgan maydonlari
 * lentaga ham, ko'rsatkichlarga ham kirmaydi.
 */
export interface AttendanceDayLike {
	workDate: string;
	status: "kelmagan" | "kelgan" | "kechikkan" | "erta_ketgan" | "davom_etmoqda" | "dam" | "uzrli";
	lateMinutes: number;
	earlyMinutes: number;
	departuresCount: number;
	/**
	 * Hududdan tashqarida o'tkazilgan daqiqalar.
	 *
	 * ⚠️ `departuresCount` dan MUSTAQIL o'lchov: bitta uzoq chiqish ham,
	 * o'nta qisqa chiqish ham bir xil sanoq beradi, lekin butunlay boshqa
	 * ma'noni anglatadi. Kanvas ikkalasini ham ko'rsatadi.
	 */
	timeOutsideMin: number;
}

export type DayFill = "ish" | "yoq" | "uzr" | "dam";

export interface DayCell {
	date: string;
	fill: DayFill;
	late: boolean;
	early: boolean;
}

/**
 * Status → lenta rangi.
 *
 * ⚠️ **Rang «ishda bo'ldimi» savoliga javob beradi, kechikish emas.**
 * Kechikish va erta ketish alohida `late`/`early` bayroqlari bo'lib,
 * katakning yuqori/pastki chetidagi chiziqcha bilan chiziladi: `#fab219`
 * va `#ec835a` oddiy ko'rishda ham ΔE 13,6 bilan ajralmaydi, ya'ni ikki
 * xil ma'noni ikki yaqin rang bilan berish adashtirardi.
 */
const FILL_BY_STATUS: Record<AttendanceDayLike["status"], DayFill> = {
	kelgan: "ish",
	kechikkan: "ish",
	// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
	erta_ketgan: "ish",
	// biome-ignore lint/style/useNamingConvention: backend status enum qiymati aynan shu
	davom_etmoqda: "ish",
	kelmagan: "yoq",
	uzrli: "uzr",
	dam: "dam",
};

/**
 * Lenta ranglari — `shared/charts/palette.ts` dan (yagona manba, spec
 * `2026-09-17-hisobotlar-analitika-design.md` §2.1).
 *
 * ⚠️ `uzr` endi uzrli yashil-moviy (`#1baf7a`), binafsha EMAS — 17.09
 * foydalanuvchi qarori, spec §2.1. Binafsha faqat «kechikkan».
 *
 * ⚠️ `dam` — holat rangi EMAS, bo'sh katak foni (`theme.ts`
 * `colorBorderSecondary`). Neytral `#8a929e` dam kunlarini ma'noli
 * holatdek to'q ko'rsatardi.
 */
export const DAY_FILL_COLORS: Record<DayFill, string> = {
	ish: HOLAT_RANGLARI.kelgan.light,
	yoq: HOLAT_RANGLARI.kelmagan.light,
	uzr: HOLAT_RANGLARI.uzrli.light,
	dam: "#eeefec",
};

/**
 * `to` bilan tugaydigan `count` kunlik oyna, o'sish tartibida.
 *
 * ⚠️ Sana arifmetikasi UTC'da bajariladi (`Date.UTC`) — mahalliy zonada
 * hisoblansa, DST o'tishi bo'lgan kunda bir kun ikki marta chiqib
 * yoki umuman tushib qolardi.
 */
export function recentDates(count: number, to: string): string[] {
	const [yil, oy, kun] = to.split("-").map(Number);
	const oxirgi = Date.UTC(yil, oy - 1, kun);
	const dates: string[] = [];

	for (let i = count - 1; i >= 0; i--) {
		dates.push(new Date(oxirgi - i * 86_400_000).toISOString().slice(0, 10));
	}

	return dates;
}

/**
 * Oynadagi HAR kun uchun bitta katak.
 *
 * ⚠️⚠️ **Qatori yo'q kun BO'LADI va u `dam` bilan to'ldiriladi.**
 * `attendance_days` — proyeksiya jadvali; uning yagona triggeri
 * `processShiftClose` (`planned_end + 30 daq`), ya'ni umuman check-in
 * qilmagan yoki smenasi hali yopilmagan kun uchun qator YO'Q. Oyna
 * to'ldirilmasa lenta uzunligi xodimdan xodimga o'zgarib, kunlar bir
 * chiziqqa tushmasdi. Neytral kulrang ATAYLAB tanlangan: bunday kun
 * haqida bizda ma'lumot YO'Q va uni «kelmagan» deb qizil qilish
 * yolg'on ayblov bo'lardi.
 */
export function toDayCells(days: AttendanceDayLike[], dates: string[]): DayCell[] {
	const byDate = new Map(days.map((day) => [day.workDate, day]));

	return dates.map((date) => {
		const day = byDate.get(date);

		return {
			date,
			fill: day ? FILL_BY_STATUS[day.status] : "dam",
			late: (day?.lateMinutes ?? 0) > 0,
			early: (day?.earlyMinutes ?? 0) > 0,
		};
	});
}

export interface EmployeeSummary {
	/** Davomat foizi, butun son. */
	rate: number;
	/**
	 * Kechikkan kunlardagi o'rtacha kechikish, daqiqa.
	 *
	 * ⚠️ Hech kim kechikmagan bo'lsa `null` — `0` EMAS. Nol «o'rtacha
	 * kechikish nol daqiqa» degan YOLG'ON da'vo bo'lardi; `null` esa
	 * «o'lchanadigan hodisa yo'q» degani va UI uni `—` bilan chizadi.
	 */
	avgLateMin: number | null;
	exits: number;
	workedDays: number;
	/** Kechikish qayd etilgan kunlar soni — `avgLateMin` ning maxraji. */
	lateDays: number;
	/** Hududdan tashqarida o'tkazilgan jami daqiqa. */
	totalOutsideMin: number;
}

/**
 * Profil ko'rsatkichlari.
 *
 * ⚠️ **`rate` bo'luvchisi — `dam` VA `uzr` dan boshqa kunlar.** Dam
 * kunini maxrajga qo'shish davomat foizini sun'iy pasaytirardi;
 * tasdiqlangan ariza esa xodimning aybi emas.
 *
 * ⚠️ Kirish massivi FAQAT haqiqiy qatorlardan iborat — `toDayCells` dagi
 * to'ldirilgan kunlar bu yerga KIRMAYDI, aks holda ma'lumot yo'q kunlar
 * maxrajni shishirib foizni jimgina pasaytirardi.
 *
 * ⚠️⚠️ **`avgLateMin` maxraji — KECHIKKAN kunlar, ishlagan kunlar EMAS.**
 * Kanvas «O'rtacha kechikish 7 daq · 3 kunda kechikkan» deb yozadi, ya'ni
 * savol «kechikkanda qancha kechikadi». Maxrajga kechikmagan kunlar
 * qo'shilsa, muntazam vaqtida keladigan, lekin bir kun 40 daqiqa
 * kechikkan xodim «2 daqiqa kechikadi» bo'lib ko'rinardi.
 */
export function summarise(days: AttendanceDayLike[]): EmployeeSummary {
	let maxraj = 0;
	let workedDays = 0;
	let kechikishJami = 0;
	let lateDays = 0;
	let exits = 0;
	let totalOutsideMin = 0;

	for (const day of days) {
		const fill = FILL_BY_STATUS[day.status];
		exits += day.departuresCount;
		totalOutsideMin += day.timeOutsideMin;

		if (fill === "dam" || fill === "uzr") {
			continue;
		}

		maxraj += 1;

		if (fill === "ish") {
			workedDays += 1;

			if (day.lateMinutes > 0) {
				lateDays += 1;
				kechikishJami += day.lateMinutes;
			}
		}
	}

	return {
		rate: maxraj === 0 ? 0 : Math.round((workedDays / maxraj) * 100),
		avgLateMin: lateDays === 0 ? null : Math.round(kechikishJami / lateDays),
		exits,
		workedDays,
		lateDays,
		totalOutsideMin,
	};
}
